import type {ReactElement} from 'react';
import React, {useState, useRef, useEffect} from 'react';
import {Image} from 'react-bootstrap';
import {getAssetUrl} from '@Utils/s3.util';
import {Text, TextSize} from '@Components/text';
import {getUniqueString} from '@Utils/string.util';
import {EmailMakerBlock} from '@Components/email-maker-block-manager/components/email-maker-block';
import type {Block, ComponentDefinition} from 'grapesjs';
import {useGrapesjsEditor, useIsMobileScreen} from '@Components/email-maker-block-manager/email-maker-block-manager-grapesjs-editor-context';
import type {UserEmailComponentTemplateWithLoadingState} from '@Components/email-maker-block-manager/email-maker-block-manager.types';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {deleteUserEmailComponentTemplate, pollForComponentTemplatePreviewChanges, updateUserEmailComponentTemplateName} from '@Libraries/email-component-template-library';
import {InputField} from '@Components/input-field';
import {useUserSavedTemplates} from '@Components/email-maker-block-manager/email-maker-block-manager.hooks';
import styles from './user-email-sections.module.scss';
import {Icon} from '@Components/icon-v2';
import {removeUserTemplate, setUserTemplatePreviewLoaded, updateUserTemplateName} from '@Components/email-maker-block-manager/email-maker-block-manager-reducer';
import {useAppDispatch} from '@/hooks';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {GA4EventName} from '@Libraries/ga-events';

type InputRefType = HTMLInputElement | HTMLTextAreaElement | null;

type UserEmailSectionData = UserEmailComponentTemplateWithLoadingState & {
  block: Block;
};

const EMPTY_SAVED_SECTION_IMAGE_WIDTH = 124;
const EMPTY_SAVED_SECTION_IMAGE_HEIGHT = 114;
const MAX_INPUT_LENGTH = 50;
const LOADING_PREVIEW_SKELETON_HEIGHT = 100;

export function UserEmailSections(): ReactElement<any> {
  const gjsEditor = useGrapesjsEditor();
  const isMobileScreen = useIsMobileScreen();
  const dispatch = useAppDispatch();
  const savedSections = useUserSavedTemplates();
  const [activeSectionBlock, setActiveSectionBlock] = useState<UserEmailSectionData | null>(null);
  const activeInputFieldRef = useRef<InputRefType>(null);
  const activeInputFieldValueRef = useRef<string>('');

  const createBlockId = (template: UserEmailComponentTemplateWithLoadingState): string => {
    return `${template.userId}_${template.id.toString()}`;
  };

  const getUserTemplates = (): UserEmailSectionData[] => {
    const userTemplates = savedSections.map((template): UserEmailSectionData => {
      return {
        ...template,
        block: gjsEditor.BlockManager.add(createBlockId(template), {
          activate: true,
          label: '',
          select: true,
          content: JSON.parse(template.jsonContent) as ComponentDefinition,
        }),
      };
    });

    userTemplates.forEach((section) => {
      gjsEditor.BlockManager.remove(section.block.getId());
    });

    return userTemplates;
  };

  const handleDeleteSavedSection = (templateId: number): void => {
    const isRecentlySavedComponentSelected = savedSections.at(0)?.id === templateId;
    if (isRecentlySavedComponentSelected) {
      gjsEditor.runCommand(window.PMW.emails.commands.REFRESH_STYLEMANAGER);
    }

    dispatch(removeUserTemplate(templateId));
    deleteUserEmailComponentTemplate(templateId);
  };

  const getSavedSectionTitle = (userBlock: UserEmailSectionData): ReactElement<any> => {
    const reference = (element: InputRefType): InputRefType => (activeInputFieldRef.current = element);

    const updateTemplateName = (): void => {
      const templateId = userBlock.id;
      const currentTemplateName = userBlock.name;
      const updatedTemplateName = activeInputFieldRef.current?.value;

      if (!updatedTemplateName || currentTemplateName === updatedTemplateName) {
        return;
      }

      dispatch(updateUserTemplateName({templateId: templateId, newName: updatedTemplateName}));
      updateUserEmailComponentTemplateName(templateId, updatedTemplateName);
    };

    const inputChangeHandler = (value: string): void => {
      activeInputFieldValueRef.current = value;
    };

    const clickHandler = (): void => {
      setActiveSectionBlock(userBlock);
      activeInputFieldValueRef.current = userBlock.name;
    };

    const blurHandler = (): void => {
      updateTemplateName();
      setActiveSectionBlock(null);
      activeInputFieldValueRef.current = '';
    };

    const submitHandler = (): void => {
      activeInputFieldRef.current?.blur();
      blurHandler();
    };

    const mobileEditClickHandler = (): void => {
      activeInputFieldRef.current?.focus();
      clickHandler();
    };

    const isInputFocused = activeSectionBlock?.id === userBlock.id;
    const showEditIcon = isMobileScreen && !isInputFocused;

    return (
      <div className={`spacing-p-l-2 spacing-p-r-2 ${styles.savedSectionTitleContainer}`}>
        {isInputFocused ? (
          <InputField
            ref={reference as unknown as React.Ref<InputRefType>}
            value={userBlock.name}
            className={styles.inputField}
            showInputIcon={false}
            inputClassName={`spacing-p-0`}
            onInputBlur={blurHandler}
            onInputSubmit={submitHandler}
            onInputChange={inputChangeHandler}
            placeholder={window.i18next.t('pmwjs_section_name')}
            maxLength={MAX_INPUT_LENGTH}
            autoFocus
          />
        ) : (
          <Text className={styles.savedSectionTitle} val={userBlock.name} size={isMobileScreen ? TextSize.XXSMALL : TextSize.SMALL} onClick={clickHandler} />
        )}
        {showEditIcon ? (
          <Icon
            className="spacing-p-0"
            icon={'icon-pencil'}
            size={IconSize.SIZE_ICON_16}
            type={IconType.GHOST_UNCOLORED}
            shape={IconShape.SQUARE}
            onClick={mobileEditClickHandler}
          />
        ) : null}
      </div>
    );
  };

  const getSavedSectionHeader = (userBlock: UserEmailSectionData): ReactElement<any> => {
    return (
      <div className={styles.savedSectionHeader}>
        {getSavedSectionTitle(userBlock)}
        <Icon
          icon={'icon-bookmark-filled'}
          className={styles.unsaveIcon}
          size={IconSize.SIZE_ICON_16}
          type={IconType.GHOST_UNCOLORED}
          shape={IconShape.SQUARE}
          onClick={() => {
            handleDeleteSavedSection(userBlock.id);
          }}
        />
      </div>
    );
  };

  const getSavedSectionBlockPreviewSkeleton = (): ReactElement<any> => {
    return <SkeletonBrick className={styles.savedSectionLoadingSkeleton} height={LOADING_PREVIEW_SKELETON_HEIGHT} />;
  };

  const getSavedSectionBlockPreview = (userBlock: UserEmailSectionData): ReactElement<any> => {
    const sourceUrl = isMobileScreen ? userBlock.mobilePreviewUrl : userBlock.desktopPreviewUrl;

    return (
      <EmailMakerBlock
        block={userBlock.block}
        extraClasses={`flex-column-justify-center ${styles.emailMakerBlock}`}
        key={getUniqueString()}
        onAddToCanvas={trackGA4EventOnBlockUse}
      >
        {userBlock.hasGif ? <video className={styles.blockVisual} src={sourceUrl} muted autoPlay playsInline loop /> : <Image src={sourceUrl} className={styles.blockVisual} />}
      </EmailMakerBlock>
    );
  };

  const getSavedSectionBlock = (userBlock: UserEmailSectionData): ReactElement<any> => {
    return userBlock.isPreviewGenerated ? getSavedSectionBlockPreview(userBlock) : getSavedSectionBlockPreviewSkeleton();
  };

  const beginPollingForPreviewChangeIfRequired = (templateData: UserEmailSectionData): void => {
    if (templateData.isPreviewGenerated) {
      return;
    }

    const previewUrl = isMobileScreen ? templateData.mobilePreviewUrl : templateData.desktopPreviewUrl;

    pollForComponentTemplatePreviewChanges(previewUrl)
      .promise.then((): void => {
        dispatch(setUserTemplatePreviewLoaded(templateData.id));
      })
      .catch((e): void => {
        console.log(e);
      });
  };

  const getSavedSection = (userBlock: UserEmailSectionData): ReactElement<any> => {
    beginPollingForPreviewChangeIfRequired(userBlock);

    return (
      <div className={styles.block} key={getUniqueString()}>
        {getSavedSectionHeader(userBlock)}
        {getSavedSectionBlock(userBlock)}
      </div>
    );
  };

  const getSavedSectionsPanel = (): ReactElement<any> => {
    return (
      <div className={styles.savedSectionsContainer}>
        {getUserTemplates().map((userBlock) => {
          return getSavedSection(userBlock);
        })}
      </div>
    );
  };

  const getSavedSectionsEmptyPanel = (): ReactElement<any> => {
    const iconElement = `<i class="icon-bookmark pmw-icon size-icon-12"></i>`;
    return (
      <div className={styles.emptySavedSectionsContainer}>
        <Image
          src={getAssetUrl('email-maker-saved-sections.png?t=1')}
          width={EMPTY_SAVED_SECTION_IMAGE_WIDTH}
          height={EMPTY_SAVED_SECTION_IMAGE_HEIGHT}
          alt={window.i18next.t('pmwjs_image')}
        />
        <div className={styles.emptySavedSectionsContainerText}>
          <Text val={window.i18next.t('pmwjs_saved_sections')} size={TextSize.MEDIUM} bold />
          <Text val={window.i18next.t('pmwjs_saved_sections_description')} size={TextSize.XSMALL} />
        </div>
        <Text val={window.i18next.t('pmwjs_save_section_sub_text', {icon: iconElement})} size={TextSize.XXSMALL} className="content-sub-text" dangerouslySetInnerHTML />
      </div>
    );
  };

  const trackGA4EventOnBlockUse = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_SAVED_SECTION_SELECT);
  };

  useEffect(() => {
    return (): void => {
      const updatedTemplateName = activeInputFieldValueRef.current;

      if (!activeSectionBlock || !updatedTemplateName) {
        return;
      }

      if (activeSectionBlock.name !== updatedTemplateName) {
        dispatch(updateUserTemplateName({templateId: activeSectionBlock.id, newName: updatedTemplateName}));
        updateUserEmailComponentTemplateName(activeSectionBlock.id, updatedTemplateName);
      }
    };
  }, [activeSectionBlock]);

  const getPanelContent = (): ReactElement<any> => {
    return savedSections.length === 0 ? getSavedSectionsEmptyPanel() : getSavedSectionsPanel();
  };

  return getPanelContent();
}
